import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  User,
  Phone,
  Mail,
  Calendar,
  Heart,
  Activity,
  Scale,
  Ruler,
  Stethoscope,
  AlertTriangle,
  FileText,
  Clock,
  Globe,
  Printer,
  Pencil,
  Save,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Contact, Patient } from '@/lib/types';
import { formatToLocalDate, getDefaultContactValue } from '@/lib/utils';
import { patientService } from '@/services/patientService';
import { getPatientIntakeStatus, getResponseDetails } from '@/services/intakeService';
import { toast } from '@/hooks/use-toast';
import { formatDOB } from '@/lib/dateUtils';
import { Package, CreditCard } from 'lucide-react';
import PatientAuditTrail from './PatientAuditTrail';
import MdiCasesSection from './MdiCasesSection';
import PatientPortalSection from './PatientPortalSection';
import { parseMedicalHistory } from '@/components/ui/medical-history-multi-select';
import MetricSparkline from './MetricSparkline';

type MetricType = 'weight' | 'height' | 'blood_pressure';

interface MetricHistoryEntry {
  id: string;
  metric_type: MetricType;
  value: Record<string, number>;
  recorded_at: string;
  recorded_by_user?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
}

interface PatientOverviewTabProps {
  patient: Patient | null;
  onPatientUpdated?: () => void;
}

const PatientOverviewTab = ({ patient, onPatientUpdated }: PatientOverviewTabProps) => {
  const [isEditingMetrics, setIsEditingMetrics] = useState(false);
  const [isSavingMetrics, setIsSavingMetrics] = useState(false);

  // Editable health metrics state
  const [editWeight, setEditWeight] = useState('');
  const [editHeightFeet, setEditHeightFeet] = useState('');
  const [editHeightInches, setEditHeightInches] = useState('');
  const [editBpSystolic, setEditBpSystolic] = useState('');
  const [editBpDiastolic, setEditBpDiastolic] = useState('');

  // Health metric history state
  const [expandedMetric, setExpandedMetric] = useState<MetricType | null>(null);
  const [metricHistory, setMetricHistory] = useState<Record<string, MetricHistoryEntry[]>>({});
  const [metricTotals, setMetricTotals] = useState<Record<string, number>>({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Intake shipping/billing address state (from intake responses)
  const [intakeAddresses, setIntakeAddresses] = useState<{
    shipping?: { line1: string; line2?: string; city: string; state: string; zip: string };
    billing?: { line1: string; line2?: string; city: string; state: string; zip: string };
    billingSameAsShipping?: boolean;
  } | null>(null);
  const [intakeAddressesLoaded, setIntakeAddressesLoaded] = useState(false);

  const fetchMetricHistory = useCallback(async () => {
    if (!patient?.id || historyLoaded) return;
    setHistoryLoading(true);
    try {
      const response = await patientService.getHealthMetricHistory(patient.id, { limit: 10 });
      if (response?.success && response?.data) {
        setMetricHistory(response.data);
        setMetricTotals(response.total || {});
      }
      setHistoryLoaded(true);
    } catch (error) {
      console.error('Failed to fetch metric history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [patient?.id, historyLoaded]);

  const toggleMetricExpand = (metric: MetricType) => {
    if (expandedMetric === metric) {
      setExpandedMetric(null);
    } else {
      setExpandedMetric(metric);
      if (!historyLoaded) {
        fetchMetricHistory();
      }
    }
  };

  // Reset history when patient data changes (after save)
  useEffect(() => {
    setHistoryLoaded(false);
    setMetricHistory({});
    setMetricTotals({});
    if (expandedMetric) {
      fetchMetricHistory();
    }
  }, [patient?.weight, patient?.height, patient?.blood_pressure_systolic, patient?.blood_pressure_diastolic]);

  // Fetch shipping/billing address from patient addresses or latest completed intake
  useEffect(() => {
    const fetchIntakeAddresses = async () => {
      if (!patient?.id) return;

      // First check if patient already has typed addresses (shipping/billing) from Address model
      const shippingAddr = patient.addresses?.find((a) => a.address_type === 'shipping');
      const billingAddr = patient.addresses?.find((a) => a.address_type === 'billing');
      if (shippingAddr) {
        setIntakeAddresses({
          shipping: {
            line1: shippingAddr.line_1 || '',
            line2: shippingAddr.line_2 || undefined,
            city: shippingAddr.city || '',
            state: shippingAddr.stateInfo?.code || shippingAddr.stateInfo?.name || '',
            zip: shippingAddr.postal_code || '',
          },
          billing: billingAddr
            ? {
                line1: billingAddr.line_1 || '',
                line2: billingAddr.line_2 || undefined,
                city: billingAddr.city || '',
                state: billingAddr.stateInfo?.code || billingAddr.stateInfo?.name || '',
                zip: billingAddr.postal_code || '',
              }
            : undefined,
          billingSameAsShipping: !billingAddr,
        });
        setIntakeAddressesLoaded(true);
        return;
      }

      // Fallback: pull from the latest completed intake response
      try {
        const statusRes = await getPatientIntakeStatus(patient.id);
        if (!statusRes.success || !statusRes.data) {
          setIntakeAddressesLoaded(true);
          return;
        }

        const allCompleted = [...(statusRes.data.completed || []), ...(statusRes.data.reviewed || [])];
        const sorted = allCompleted
          .filter((i) => i.responseId || i.questionnaireResponseId || i.questionnaire_response_id)
          .sort((a, b) => new Date(b.completedAt || b.createdAt).getTime() - new Date(a.completedAt || a.createdAt).getTime());

        if (sorted.length === 0) {
          setIntakeAddressesLoaded(true);
          return;
        }

        const responseId = sorted[0].responseId || sorted[0].questionnaireResponseId || sorted[0].questionnaire_response_id;
        if (!responseId) {
          setIntakeAddressesLoaded(true);
          return;
        }

        const detailRes = await getResponseDetails(responseId);
        if (!detailRes.success || !detailRes.data?.responses) {
          setIntakeAddressesLoaded(true);
          return;
        }

        const responses = detailRes.data.responses;
        const getValue = (key: string): string => {
          if (Array.isArray(responses)) {
            const item = responses.find((r) => r.questionId === key);
            return item ? String(item.value || '') : '';
          }
          return String((responses as Record<string, unknown>)[key] || '');
        };

        // Try camelCase keys first (page-based intakes), fall back to snake_case (template-based)
        const shippingLine1 = getValue('shippingAddressLine1') || getValue('shipping_address_line1');
        if (shippingLine1) {
          const shipping = {
            line1: shippingLine1,
            line2: getValue('shippingAddressLine2') || getValue('shipping_address_line2') || undefined,
            city: getValue('shippingCity') || getValue('shipping_city'),
            state: getValue('shippingState') || getValue('shipping_state'),
            zip: getValue('shippingPostalCode') || getValue('shipping_postal_code'),
          };

          const billingSame = getValue('billingSameAsShipping') || getValue('billing_same_as_shipping');
          const isSame = billingSame === 'true' || billingSame === true.toString();

          const billingLine1 = getValue('billingAddressLine1') || getValue('billing_address_line1');
          const billing = billingLine1 && !isSame
            ? {
                line1: billingLine1,
                line2: getValue('billingAddressLine2') || getValue('billing_address_line2') || undefined,
                city: getValue('billingCity') || getValue('billing_city'),
                state: getValue('billingState') || getValue('billing_state'),
                zip: getValue('billingPostalCode') || getValue('billing_postal_code'),
              }
            : undefined;

          setIntakeAddresses({ shipping, billing, billingSameAsShipping: isSame });
        }
      } catch (error) {
        console.error('Failed to fetch intake addresses:', error);
      } finally {
        setIntakeAddressesLoaded(true);
      }
    };

    fetchIntakeAddresses();
  }, [patient?.id, patient?.addresses]);

  if (!patient) {
    return (
      <div className="text-center py-8 text-slate-500">
        No patient data available
      </div>
    );
  }

  const getSparklineData = (type: MetricType): { date: string; value: number }[] => {
    const entries = metricHistory[type] || [];
    return entries
      .map((e) => {
        let val = 0;
        if (type === 'weight') val = e.value.weight;
        else if (type === 'height') val = e.value.height;
        else if (type === 'blood_pressure') val = e.value.systolic;
        return { date: e.recorded_at, value: val };
      })
      .filter((d) => d.value > 0);
  };

  const formatMetricValue = (entry: MetricHistoryEntry): string => {
    if (entry.metric_type === 'weight') return `${entry.value.weight} lbs`;
    if (entry.metric_type === 'height') {
      const totalInches = entry.value.height;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return `${feet} ft ${inches} in`;
    }
    if (entry.metric_type === 'blood_pressure') return `${entry.value.systolic}/${entry.value.diastolic} mmHg`;
    return '—';
  };

  const convertHeightToFtIn = (height: string | undefined) => {
    if (height === undefined) return '—';
    let value = parseFloat(height.toString());
    if (isNaN(value)) return '—';
    const totalInches = value;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet} ft ${inches} in`;
  };

  function calculateBMI(weight: number, height: number, isMetric = true) {
    let bmi: number = 0;
    if (isMetric) {
      let heightInMeters = height / 100;
      bmi = weight / (heightInMeters * heightInMeters);
    } else {
      bmi = (weight * 703) / (height * height);
    }
    return parseFloat(bmi.toFixed(2));
  }

  function getBMICategory(bmi: number) {
    if (bmi < 18.5) {
      return 'Underweight';
    } else if (bmi >= 18.5 && bmi < 25) {
      return 'Normal weight';
    } else if (bmi >= 25 && bmi < 30) {
      return 'Overweight';
    } else {
      return 'Obesity';
    }
  }

  // Start editing: populate fields from current patient data
  const handleStartEditing = () => {
    setEditWeight(patient.weight ? patient.weight.toString() : '');

    if (patient.height) {
      const totalInches = parseFloat(patient.height.toString());
      if (!isNaN(totalInches)) {
        setEditHeightFeet(Math.floor(totalInches / 12).toString());
        setEditHeightInches(Math.round(totalInches % 12).toString());
      } else {
        setEditHeightFeet('');
        setEditHeightInches('');
      }
    } else {
      setEditHeightFeet('');
      setEditHeightInches('');
    }

    setEditBpSystolic(patient.blood_pressure_systolic ? patient.blood_pressure_systolic.toString() : '');
    setEditBpDiastolic(patient.blood_pressure_diastolic ? patient.blood_pressure_diastolic.toString() : '');

    setIsEditingMetrics(true);
  };

  const handleCancelEditing = () => {
    setIsEditingMetrics(false);
  };

  // Compute preview BMI from edit fields
  const getEditBMI = () => {
    const w = parseFloat(editWeight);
    const ft = parseFloat(editHeightFeet) || 0;
    const inch = parseFloat(editHeightInches) || 0;
    const totalInches = ft * 12 + inch;
    if (w > 0 && totalInches > 0) {
      return calculateBMI(w, totalInches, false);
    }
    return null;
  };

  const handleSaveMetrics = async () => {
    setIsSavingMetrics(true);
    try {
      const ft = parseFloat(editHeightFeet) || 0;
      const inch = parseFloat(editHeightInches) || 0;
      const totalInches = ft > 0 || inch > 0 ? ft * 12 + inch : '';

      const today = new Date().toISOString().split('T')[0];

      const updateData: Record<string, any> = {
        weight: editWeight || '',
        height: totalInches !== '' ? totalInches : '',
        weight_recorded_date: editWeight ? today : '',
        blood_pressure_systolic: editBpSystolic ? parseInt(editBpSystolic) : '',
        blood_pressure_diastolic: editBpDiastolic ? parseInt(editBpDiastolic) : '',
        blood_pressure_recorded_at: (editBpSystolic || editBpDiastolic) ? today : '',
      };

      await patientService.updatePatient(patient.id, updateData);

      toast({
        title: 'Health metrics updated',
        description: 'Patient health metrics have been saved successfully.',
      });

      setIsEditingMetrics(false);
      onPatientUpdated?.();
    } catch (error) {
      console.error('Error saving health metrics:', error);
      toast({
        title: 'Error',
        description: 'Failed to save health metrics. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingMetrics(false);
    }
  };

  // Blood pressure interpretation
  const getBPCategory = (systolic: number, diastolic: number) => {
    if (systolic < 120 && diastolic < 80) return { label: 'Normal', color: 'text-green-600' };
    if (systolic < 130 && diastolic < 80) return { label: 'Elevated', color: 'text-yellow-600' };
    if (systolic < 140 || diastolic < 90) return { label: 'High (Stage 1)', color: 'text-orange-600' };
    if (systolic >= 140 || diastolic >= 90) return { label: 'High (Stage 2)', color: 'text-red-600' };
    return { label: '', color: '' };
  };

  const formatAddress = (address: import('@/lib/types').Address) =>
    [
      address.line_1,
      address.line_2,
      address.line_3 ? `${address.line_3}` : null,
      address.city,
      address.stateInfo?.name || address.stateInfo?.code,
      address.postal_code,
      address.country,
    ]
      .filter(Boolean)
      .join(', ');

  const shippingAddress = patient.addresses?.find((a) => a.address_type === 'shipping');
  const billingAddress = patient.addresses?.find((a) => a.address_type === 'billing');
  // Fallback: if no address_type is set, use any address
  const fallbackAddress = !shippingAddress && !billingAddress
    ? patient.addresses?.[0]
    : null;

  const fullAddress =
    patient.addresses && patient.addresses.length > 0
      ? (fallbackAddress ? formatAddress(fallbackAddress) : null)
      : null;

  const getContactValue = (key: string): Array<Contact> =>
    patient.contacts?.filter((c) => c.key === key);

  const emailContacts = getContactValue('email');
  const phoneContacts = getContactValue('phone');

  const preferredMethods = [
    { key: 'email', label: 'Email', enabled: !!patient.preferred_contact_methods?.email },
    { key: 'sms', label: 'SMS', enabled: !!patient.preferred_contact_methods?.sms },
    { key: 'phone', label: 'Phone', enabled: !!patient.preferred_contact_methods?.phone },
  ].filter((method) => method.enabled);


  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Personal Information */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#4A8A9A' }}
              >
                <User className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  Personal Information
                </h3>
                <p className="text-xs text-slate-500">Basic patient details</p>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Date of Birth - Featured */}
            <div
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'rgb(245, 247, 247)' }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Date of Birth
                </span>
              </div>
              <p className="text-lg font-semibold text-slate-900">
                {formatDOB(patient.dob)}
              </p>
            </div>

            {/* Gender & Species */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgb(245, 247, 247)' }}
                >
                  <User className="h-4 w-4 text-slate-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                    Gender
                  </p>
                  <p className="text-sm text-slate-900 capitalize">
                    {patient.gender || '—'}
                  </p>
                </div>
              </div>

              {/* <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgb(245, 247, 247)' }}
                >
                  <Heart className="h-4 w-4 text-slate-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                    Species
                  </p>
                  <p className="text-sm text-slate-900 capitalize">
                    {patient.species || '—'}
                  </p>
                </div>
              </div> */}
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgb(245, 247, 247)' }}
                >
                  <Stethoscope className="h-4 w-4 text-slate-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                    Assigned Practitioner
                  </p>
                  <p className="text-sm text-slate-900">
                    {patient?.practitioner?.full_name || '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Assigned Practitioner & Last Updated */}
            <div className="grid grid-cols-2 gap-4">


              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgb(245, 247, 247)' }}
                >
                  <Clock className="h-4 w-4 text-slate-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                    Last Updated
                  </p>
                  <p className="text-sm text-slate-900">
                    {formatToLocalDate(patient.updated_at, 'MM/DD/YYYY, hh:mm a')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#BD6F51' }}
              >
                <Mail className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  Contact Information
                </h3>
                <p className="text-xs text-slate-500">Communication details</p>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              {emailContacts.map((contact) => (
                <div key={contact.id} className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgb(245, 247, 247)' }}
                  >
                    <Mail className="h-4 w-4 text-slate-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                      Email
                      {contact.is_default && <Badge className="h-5 rounded-full bg-slate-100 px-1.5 text-[10px] font-medium text-slate-600 border-0">
                        Default
                      </Badge>}
                    </div>
                    <p className="text-sm text-slate-900 truncate">
                      {contact.value}
                    </p>
                  </div>
                </div>))}



              {/* Phone */}
              {phoneContacts.map((contact) => (
                <div key={contact.id} className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgb(245, 247, 247)' }}
                  >
                    <Phone className="h-4 w-4 text-slate-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                      Phone
                      {contact.is_default && <Badge className="h-5 rounded-full bg-slate-100 px-1.5 text-[10px] font-medium text-slate-600 border-0">
                        Default
                      </Badge>}
                    </div>
                    <p className="text-sm text-slate-900 truncate">
                      {contact.value}
                    </p>
                  </div>
                </div>))}

            </div>

            {/* Preferred Contact Method */}
            <div className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgb(245, 247, 247)' }}
              >
                <Heart className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                  Preferred Contact Method
                </p>
                {preferredMethods.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {preferredMethods.map((method) => (
                      <Badge
                        key={method.key}
                        className="bg-teal-50 text-teal-700 border border-teal-100 text-xs font-medium"
                      >
                        {method.label}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-900">Not specified</p>
                )}
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgb(245, 247, 247)' }}
              >
                <AlertTriangle className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                  Emergency Contact
                </p>
                <p className="text-sm text-slate-900 truncate">
                  {patient.emergency_contact_phone || '—'}
                </p>
              </div>
            </div>

            {/* Shipping Address */}
            <div
              className="flex items-start gap-3 p-3 rounded-lg"
              style={{ backgroundColor: 'rgb(245, 247, 247)' }}
            >
              <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 bg-white">
                <CreditCard className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                  Billing Address
                </p>
                <p className="text-sm text-slate-900 leading-relaxed">
                  {billingAddress
                    ? formatAddress(billingAddress)
                    : fallbackAddress
                      ? formatAddress(fallbackAddress)
                      : intakeAddresses?.billing
                        ? [
                            intakeAddresses.billing.line1,
                            intakeAddresses.billing.line2,
                            intakeAddresses.billing.city,
                            intakeAddresses.billing.state,
                            intakeAddresses.billing.zip,
                          ].filter(Boolean).join(', ')
                        : intakeAddresses?.billingSameAsShipping && intakeAddresses?.shipping
                          ? [
                              intakeAddresses.shipping.line1,
                              intakeAddresses.shipping.line2,
                              intakeAddresses.shipping.city,
                              intakeAddresses.shipping.state,
                              intakeAddresses.shipping.zip,
                            ].filter(Boolean).join(', ')
                          : 'No address on file'}
                </p>
              </div>
            </div>

            {/* Shipping Address */}
            <div
              className="flex items-start gap-3 p-3 rounded-lg"
              style={{ backgroundColor: 'rgb(245, 247, 247)' }}
            >
              <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 bg-white">
                <Package className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                  Shipping Address
                </p>
                <p className="text-sm text-slate-900 leading-relaxed">
                  {shippingAddress
                    ? formatAddress(shippingAddress)
                    : intakeAddresses?.shipping
                      ? [
                          intakeAddresses.shipping.line1,
                          intakeAddresses.shipping.line2,
                          intakeAddresses.shipping.city,
                          intakeAddresses.shipping.state,
                          intakeAddresses.shipping.zip,
                        ].filter(Boolean).join(', ')
                      : billingAddress
                        ? (<>{formatAddress(billingAddress)} <span className="text-xs text-slate-400">(Same as billing)</span></>)
                        : fallbackAddress
                          ? (<>{formatAddress(fallbackAddress)} <span className="text-xs text-slate-400">(Same as billing)</span></>)
                          : 'No address on file'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Health Metrics */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#2563eb' }}
              >
                <Activity className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  Health Metrics
                </h3>
                <p className="text-xs text-slate-500">Physical measurements & vitals</p>
              </div>
            </div>
            {!isEditingMetrics ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={handleStartEditing}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs"
                  onClick={handleCancelEditing}
                  disabled={isSavingMetrics}
                >
                  <X className="h-3.5 w-3.5" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="gap-1.5 text-xs"
                  onClick={handleSaveMetrics}
                  disabled={isSavingMetrics}
                >
                  {isSavingMetrics ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  Save
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="p-5">
          {isEditingMetrics ? (
            /* ── EDIT MODE ── */
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Weight Input */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Weight
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="1500"
                      step="0.1"
                      value={editWeight}
                      onChange={(e) => setEditWeight(e.target.value)}
                      placeholder="Enter weight"
                      className="flex-1"
                    />
                    <span className="text-sm text-slate-500 font-bold">lbs</span>
                  </div>
                </div>

                {/* Height Input */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Height
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="8"
                      step="1"
                      value={editHeightFeet}
                      onChange={(e) => setEditHeightFeet(e.target.value)}
                      placeholder="Feet"
                      className="flex-1"
                    />
                    <span className="text-sm text-slate-500 font-bold">ft</span>
                    <Input
                      type="number"
                      min="0"
                      max="11"
                      step="1"
                      value={editHeightInches}
                      onChange={(e) => setEditHeightInches(e.target.value)}
                      placeholder="In"
                      className="flex-1"
                    />
                    <span className="text-sm text-slate-500 font-bold">in</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Blood Pressure Input */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Blood Pressure
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="50"
                      max="300"
                      step="1"
                      value={editBpSystolic}
                      onChange={(e) => setEditBpSystolic(e.target.value)}
                      placeholder="Systolic"
                      className="flex-1"
                    />
                    <span className="text-lg text-slate-400 font-bold">/</span>
                    <Input
                      type="number"
                      min="30"
                      max="200"
                      step="1"
                      value={editBpDiastolic}
                      onChange={(e) => setEditBpDiastolic(e.target.value)}
                      placeholder="Diastolic"
                      className="flex-1"
                    />
                    <span className="text-sm text-slate-500 font-bold">mmHg</span>
                  </div>
                </div>

                {/* BMI Preview (auto-calculated) */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    BMI (auto-calculated)
                  </label>
                  <div
                    className="p-2.5 rounded-md border border-slate-200"
                    style={{ backgroundColor: 'rgb(245, 247, 247)' }}
                  >
                    {getEditBMI() ? (
                      <span className="text-sm font-semibold text-slate-900">
                        {getEditBMI()} — {getBMICategory(getEditBMI()!)}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400">
                        Enter weight and height to calculate
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ── DISPLAY MODE ── */
            <div className="space-y-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Weight */}
                <div
                  className="p-4 rounded-lg cursor-pointer transition-colors hover:bg-slate-100"
                  style={{ backgroundColor: expandedMetric === 'weight' ? 'rgb(237, 240, 240)' : 'rgb(245, 247, 247)' }}
                  onClick={() => toggleMetricExpand('weight')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Scale className="h-4 w-4 text-slate-400" />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Weight
                      </span>
                    </div>
                    {expandedMetric === 'weight' ? (
                      <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                    )}
                  </div>
                  <p className="text-lg font-semibold text-slate-900">
                    {patient.weight ? `${patient.weight} lbs` : '—'}
                  </p>
                  {patient.weight_recorded_at && (
                    <p className="text-xs text-slate-500 mt-1">
                      Recorded {formatToLocalDate(patient.weight_recorded_at, 'MM/DD/YYYY')}
                    </p>
                  )}
                </div>

                {/* Height */}
                <div
                  className="p-4 rounded-lg cursor-pointer transition-colors hover:bg-slate-100"
                  style={{ backgroundColor: expandedMetric === 'height' ? 'rgb(237, 240, 240)' : 'rgb(245, 247, 247)' }}
                  onClick={() => toggleMetricExpand('height')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Ruler className="h-4 w-4 text-slate-400" />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Height
                      </span>
                    </div>
                    {expandedMetric === 'height' ? (
                      <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                    )}
                  </div>
                  <p className="text-lg font-semibold text-slate-900">
                    {patient.height ? convertHeightToFtIn(patient.height) : '—'}
                  </p>
                </div>

                {/* BMI */}
                <div
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: 'rgb(245, 247, 247)' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="h-4 w-4 text-slate-400" />
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      BMI
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-slate-900">
                    {patient.weight && patient.height
                      ? calculateBMI(
                        parseFloat(patient.weight.toString()),
                        parseFloat(patient.height.toString()),
                        false
                      )
                      : '—'}
                  </p>
                  {patient.weight && patient.height && (
                    <p className="text-xs text-slate-500 mt-1">
                      {getBMICategory(
                        calculateBMI(
                          parseFloat(patient.weight.toString()),
                          parseFloat(patient.height.toString()),
                          false
                        )
                      )}
                    </p>
                  )}
                </div>

                {/* Blood Pressure */}
                <div
                  className="p-4 rounded-lg cursor-pointer transition-colors hover:bg-slate-100"
                  style={{ backgroundColor: expandedMetric === 'blood_pressure' ? 'rgb(237, 240, 240)' : 'rgb(245, 247, 247)' }}
                  onClick={() => toggleMetricExpand('blood_pressure')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4 text-slate-400" />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Blood Pressure
                      </span>
                    </div>
                    {expandedMetric === 'blood_pressure' ? (
                      <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                    )}
                  </div>
                  <p className="text-lg font-semibold text-slate-900">
                    {patient.blood_pressure_systolic && patient.blood_pressure_diastolic
                      ? `${patient.blood_pressure_systolic}/${patient.blood_pressure_diastolic}`
                      : '—'}
                  </p>
                  {patient.blood_pressure_systolic && patient.blood_pressure_diastolic && (
                    <>
                      <p className="text-xs text-slate-500 mt-1">
                        <span className="text-slate-400">mmHg</span>
                        {' · '}
                        <span className={getBPCategory(patient.blood_pressure_systolic, patient.blood_pressure_diastolic).color}>
                          {getBPCategory(patient.blood_pressure_systolic, patient.blood_pressure_diastolic).label}
                        </span>
                      </p>
                      {patient.blood_pressure_recorded_at && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          Recorded {formatToLocalDate(patient.blood_pressure_recorded_at, 'MM/DD/YYYY')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Expanded History Panel */}
              {expandedMetric && (
                <div
                  className="rounded-lg border border-slate-200 p-4 animate-in slide-in-from-top-2 duration-200"
                  style={{ backgroundColor: 'rgb(250, 251, 251)' }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      {expandedMetric === 'weight' && 'Weight History'}
                      {expandedMetric === 'height' && 'Height History'}
                      {expandedMetric === 'blood_pressure' && 'Blood Pressure History'}
                    </h4>
                    {(metricTotals[expandedMetric] || 0) > 10 && (
                      <span className="text-xs text-slate-400">
                        Showing 10 of {metricTotals[expandedMetric]}
                      </span>
                    )}
                  </div>

                  {historyLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      <span className="text-xs text-slate-400 ml-2">Loading history...</span>
                    </div>
                  ) : (metricHistory[expandedMetric] || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">
                      No history recorded yet
                    </p>
                  ) : (
                    <>
                      {/* Sparkline */}
                      {getSparklineData(expandedMetric).length >= 2 && (
                        <div className="flex justify-center mb-3 pb-3 border-b border-slate-100">
                          <MetricSparkline
                            data={getSparklineData(expandedMetric)}
                            color={expandedMetric === 'blood_pressure' ? '#ef4444' : expandedMetric === 'weight' ? '#2563eb' : '#059669'}
                            width={320}
                            height={56}
                          />
                        </div>
                      )}

                      {/* History List */}
                      <div className="space-y-1.5">
                        {(metricHistory[expandedMetric] || []).map((entry) => (
                          <div
                            key={entry.id}
                            className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-slate-800">
                                {formatMetricValue(entry)}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-400">
                              {entry.recorded_by_user && (
                                <span>
                                  {entry.recorded_by_user.first_name} {entry.recorded_by_user.last_name}
                                </span>
                              )}
                              <span>{formatToLocalDate(entry.recorded_at, 'MM/DD/YYYY')}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* General Note */}
      {patient.note && (
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#6366f1' }}
              >
                <FileText className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">General Note</h3>
                <p className="text-xs text-slate-500">Additional patient notes</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <p className="text-sm text-slate-700 leading-relaxed">{patient.note}</p>
          </div>
        </div>
      )}

      {/* Medication Allergies */}
      <div className="bg-red-50 rounded-lg border border-red-200">
        <div className="px-5 py-4 border-b border-red-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-500">
              <AlertTriangle className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-red-900">
                Medication Allergies
              </h3>
              <p className="text-xs text-red-600">Known drug allergies & sensitivities</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          {patient.medication_allergies && patient.medication_allergies.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {patient.medication_allergies.map((allergy, idx) => (
                <Badge
                  key={idx}
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-red-100 text-red-700 border-0 hover:bg-red-100"
                >
                  {allergy}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Badge className="text-xs font-medium px-3 py-1.5 rounded-full bg-green-100 text-green-700 border-0">
                No Known Allergies
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* Medical History */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#059669' }}
            >
              <Stethoscope className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Medical History</h3>
              <p className="text-xs text-slate-500">Past medical conditions & history</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          {patient.medical_history ? (
            (() => {
              const parsed = parseMedicalHistory(patient.medical_history);
              const hasConditions = parsed.conditions.length > 0;
              const hasOther = parsed.other.trim().length > 0;
              return (
                <div className="space-y-3">
                  {hasConditions && (
                    <div className="flex flex-wrap gap-1.5">
                      {parsed.conditions.map((condition) => (
                        <Badge
                          key={condition}
                          className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 text-xs font-medium px-2.5 py-1"
                        >
                          {condition}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {hasOther && (
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {hasConditions && <span className="text-xs font-medium text-slate-500 block mb-1">Other:</span>}
                      {parsed.other}
                    </p>
                  )}
                  {!hasConditions && !hasOther && (
                    <p className="text-sm text-slate-500">No medical history on file</p>
                  )}
                </div>
              );
            })()
          ) : (
            <p className="text-sm text-slate-500">No medical history on file</p>
          )}
        </div>
      </div>

      {/* MDI Telehealth Cases Section */}
      {patient?.id && (
        <MdiCasesSection patientId={patient.id} />
      )}

      {/* Patient Portal Section */}
      {patient?.id && (
        <PatientPortalSection
          patientId={patient.id}
          patientEmail={getDefaultContactValue(patient.contacts, 'email')}
          patientPhone={getDefaultContactValue(patient.contacts, 'phone')}
        />
      )}

      {/* Activity Timeline Section */}
      {patient?.id && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Recent Activity</h3>
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-700 font-medium"
              onClick={() => navigate(`/patients/${patient.id}?tab=timeline`)}
            >
              View All
            </button>
          </div>
          <PatientAuditTrail
            patientId={patient.id}
            limit={10}
            enablePagination={false}
            showHeader={false}
            showFilters={false}
          />
        </div>
      )}
    </div>
  );
};

export default PatientOverviewTab;
